/**
 * Leave Statement Builder
 *
 * Derives a "bank statement"–style transaction ledger for a leave-type
 * account from the data already available client-side:
 *   - The employee's leave balances (entitled / accrued / carried-forward)
 *   - The employee's leave applications for the year
 *
 * Mental model (employee-facing): each leave type with a balance is a
 * savings account.  Credits are deposits (carry-forward, annual entitlement,
 * monthly accrual, OIL claims).  Debits are withdrawals (approved leave
 * applications and pending reservations).  The closing balance equals the
 * current `remaining_days` reported by the backend.
 *
 * This is a presentation-layer derivation -- there is no dedicated ledger
 * endpoint.  We reconcile against `remaining_days` so the running total at
 * the end of the statement always matches the authoritative balance the
 * user sees on their balance card.
 */

import { getAppliedLeaveTypeName } from './leaveDisplayUtils';

const ACCRUAL_BASED_TYPES = new Set(['Annual Leave']);

const isAccrualBased = (leaveTypeName, isPartTime) => {
  if (isPartTime) return true;
  return ACCRUAL_BASED_TYPES.has(leaveTypeName);
};

/**
 * Event-driven leave types: the entitlement is NOT a flat calendar-year
 * grant on 1 Jan.  It is unlocked by a life/service event (a child's birth
 * certificate, a marriage certificate, an NS call-up) and only appears once
 * the employee is eligible.  For these, the statement must show the grant as
 * a transaction dated to WHEN the employee became eligible — not Jan 1 — so
 * the ledger reads truthfully ("Childcare Leave appeared when I uploaded my
 * child's birth cert").
 */
const EVENT_DRIVEN_LEAVE_TYPES = new Set([
  'Maternity Leave',
  'Paternity Leave',
  'Shared Parental Leave',
  'Childcare Leave',
  'Childcare Leave (Non-Citizen)',
  'Extended Childcare Leave',
  'Marriage Leave',
  'Unpaid Infant Care Leave',
  'National Service Leave',
]);

const isEventDrivenLeave = (leaveTypeName) =>
  EVENT_DRIVEN_LEAVE_TYPES.has(leaveTypeName);

/**
 * Resolve the date an event-driven entitlement was granted, within the
 * statement year.  Prefers the balance row's creation timestamp (event-based
 * balances are materialised at the moment the employee becomes eligible — the
 * most accurate "it appeared in my account" signal), then the child's birth
 * date from carry-forward provenance, then the backend eligibility date.
 * Falls back to the period start when the grant happened in a prior year
 * (i.e. it was carried into this statement).
 */
const resolveGrantDate = (balance, year, fallbackDate) => {
  const candidates = [
    balance?.created_at,
    balance?.carry_forward_origin?.child_birth_date,
    balance?.eligibility_date,
  ];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const parsed = new Date(candidate);
    if (!Number.isNaN(parsed.getTime()) && parsed.getFullYear() === year) {
      // Normalise to local midnight so it sorts/compares stably alongside
      // the other day-only transaction dates.
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
  }
  return fallbackDate;
};

const toNumber = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const round1 = (n) => Math.round(n * 10) / 10;

/**
 * Parse a YYYY-MM-DD (or full ISO) value into a local-midnight Date so it
 * compares stably against other local-midnight dates regardless of timezone.
 * Returns null when the value is missing or unparseable.
 */
const parseDateOnly = (value) => {
  if (!value) return null;
  const ymd = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    return new Date(parseInt(ymd[1], 10), parseInt(ymd[2], 10) - 1, parseInt(ymd[3], 10));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Resolve the days that a single leave application consumed from the
 * given leave-type account (by name).  Honours the consumption proposal
 * for cross-type splits (e.g. an Annual Leave request that drew partly
 * from OIL).  Falls back to `leave_total_days` when the proposal is
 * absent and the application's own type matches the account.
 */
const consumedDaysForAccount = (application, accountName, accountYear) => {
  const items = application?.leave_consumption_proposal?.items;
  if (Array.isArray(items) && items.length > 0) {
    let total = 0;
    items.forEach((item) => {
      const rawItemYear = Number(item?.balance_year);
      const itemYear = Number.isFinite(rawItemYear)
        ? rawItemYear
        : parseDateOnly(application?.leave_start_date)?.getFullYear();
      if (
        (item?.leave_type || '').trim() === accountName
        && itemYear === accountYear
      ) {
        total += toNumber(item.days);
      }
    });
    return round1(total);
  }

  // No proposal -- treat as a single-account consumption when the
  // application's own type matches.
  const appliedName = getAppliedLeaveTypeName(application);
  const applicationYear = parseDateOnly(
    application?.leave_start_date
  )?.getFullYear();
  if (appliedName === accountName && applicationYear === accountYear) {
    return round1(toNumber(application.leave_total_days || application.total_days));
  }
  return 0;
};

/**
 * Format a YYYY-MM-DD ISO string (or Date) as a comparable timestamp.
 * We use `T00:00:00` so day-only ISOs sort/compare consistently regardless
 * of the runtime timezone.
 */
const toSortKey = (isoOrDate) => {
  if (!isoOrDate) return 0;
  if (isoOrDate instanceof Date) return isoOrDate.getTime();
  const s = String(isoOrDate);
  // YYYY-MM-DD -- treat as local midnight to avoid TZ flip
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d).getTime();
  }
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : 0;
};

const APP_STATUSES_THAT_DEDUCT = new Set([
  'approved',
  'auto_approved',
]);
const APP_STATUSES_PENDING = new Set([
  'submitted',
  'pending',
]);

/**
 * List the accrual posting dates that have already occurred within the given
 * statement year for an accrual-based leave type.
 *
 *   - Full-time staff: posts on the employee's monthsary (the same calendar
 *     day-of-month as their employment start date).  Months shorter than
 *     the start day fall back to the last day of that month — mirroring
 *     the backend `is_monthsary_today()` rule.  The first monthsary is the
 *     month FOLLOWING the start month (you must complete a full month of
 *     service before the first accrual posts).
 *   - Part-time staff: posts on the 1st of every calendar month (aligned
 *     with payroll).  The 1st of the start month does NOT post (no
 *     completed month yet) — first posting is the 1st of the month AFTER
 *     the start month.
 *
 * For the current year, only dates up to and including today are returned.
 * For past years, all eligible monthsaries up to Dec 31 are returned.
 */
const computeMonthsaryDates = ({ year, employmentStartDate, isPartTime }) => {
  if (!employmentStartDate) return [];

  // Parse YYYY-MM-DD as local midnight so comparisons against
  // `new Date(year, mo, day)` (also local midnight) are TZ-stable.
  // Falls back to native parse for full ISO timestamps.
  let start;
  const ymd = String(employmentStartDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    start = new Date(parseInt(ymd[1], 10), parseInt(ymd[2], 10) - 1, parseInt(ymd[3], 10));
  } else {
    start = new Date(employmentStartDate);
  }
  if (Number.isNaN(start.getTime())) return [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isCurrentYear = year === today.getFullYear();
  const isFutureYear = year > today.getFullYear();
  if (isFutureYear) return [];

  const lastMonth = isCurrentYear ? today.getMonth() : 11;

  // Helper: clamp the start day to the last day of the target month
  // (e.g. start day 31 → 30 in Apr, 28/29 in Feb).
  const clampDay = (yr, mo, day) => {
    const lastDay = new Date(yr, mo + 1, 0).getDate();
    return Math.min(day, lastDay);
  };

  const result = [];
  for (let mo = 0; mo <= lastMonth; mo += 1) {
    const day = isPartTime ? 1 : clampDay(year, mo, start.getDate());
    const candidate = new Date(year, mo, day);

    // Need at least one full month of service before the first posting.
    // For full-time: candidate must be strictly after the employment start.
    // For part-time: same rule — 1st of the start month doesn't count.
    if (candidate <= start) continue;

    // For the current year & current month, skip if the monthsary hasn't
    // arrived yet (e.g. start day 28, today is the 15th → skip).
    if (isCurrentYear && mo === today.getMonth() && candidate > today) continue;

    result.push(candidate);
  }
  return result;
};

/**
 * Build a chronologically-sorted statement of transactions for one
 * leave-type "account" within a given calendar year.
 *
 * Returns an object:
 *   {
 *     accountName,                // 'Annual Leave'
 *     periodStart, periodEnd,     // Date objects (Jan 1 / Dec 31 of year)
 *     opening,                    // opening balance (carry-forward etc.)
 *     totalCredits,
 *     totalDebits,
 *     totalPending,               // pending debits not yet approved
 *     closing,                    // running balance after approved tx
 *     transactions: [             // sorted ascending by date
 *       {
 *         id, date, sortDate,
 *         kind: 'opening'|'credit'|'debit'|'pending',
 *         title,            short label
 *         description,      one-line secondary text
 *         reference,        optional small chip (e.g. "AUTO-ACCRUAL")
 *         amount,           positive number (sign rendered by the row)
 *         balance,          running approved balance after this row
 *         leaveApplication, // optional source application
 *         status,           // optional leave_status for chips
 *       },
 *       ...
 *     ]
 *   }
 */
export const buildAccountStatement = ({
  balance,
  applications,
  year,
  user,
}) => {
  const accountName = balance?.leave_type?.leave_type_name || '';
  const isPartTime = user?.employment_type === 'part_time';
  const accrualBased = isAccrualBased(accountName, isPartTime);

  const periodStart = new Date(year, 0, 1);
  const periodEnd = new Date(year, 11, 31);

  // Annual Leave 3-month probation gate (MOM-aligned).  The system keeps
  // accruing AL from day one (the balance grows in the background), but an
  // employee on probation cannot yet USE it — so surfacing "+1.5 earned"
  // accrual rows is noise that implies an availability they don't have.
  // We therefore DEFER the per-month accrual history from the ledger until
  // the employee completes 3 months of service.  Once that date passes, the
  // full accrual history (including the probation months that are now usable)
  // appears.  Gated on "today", not the statement year, so a past year viewed
  // after the employee has vested shows its accruals.
  const employmentStart = parseDateOnly(user?.employment_start_date);
  const accrualEligibilityDate = employmentStart
    ? new Date(
        employmentStart.getFullYear(),
        employmentStart.getMonth() + 3,
        employmentStart.getDate()
      )
    : null;
  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);
  const accrualDisplayEligible =
    !accrualEligibilityDate || todayMidnight >= accrualEligibilityDate;
  const accrualDeferred =
    accrualBased && accountName === 'Annual Leave' && !accrualDisplayEligible;

  const carriedForward = round1(toNumber(balance.carried_forward_days));
  const entitledDays = round1(toNumber(balance.entitled_days));
  const accruedDays = round1(toNumber(balance.accrued_days));
  const totalAvailable = round1(
    toNumber(balance.total_available_days ?? balance.entitled_days)
  );

  const transactions = [];

  // 1. Opening: carry-forward (if any)
  if (carriedForward > 0) {
    transactions.push({
      id: `${balance.id || accountName}-cf`,
      sortDate: new Date(year, 0, 1).getTime() - 2,
      date: periodStart,
      kind: 'opening',
      title: 'Brought forward',
      description: `Unused ${accountName.toLowerCase()} carried over from ${year - 1}`,
      reference: 'CARRY-FWD',
      amount: carriedForward,
    });
  }

  // 2. Annual entitlement / accrual credits
  //    - Non-accrual types: full annual entitlement granted on Jan 1
  //    - Accrual-based types: one credit per monthsary (or per 1st of month
  //      for part-timers) so the statement reads like a real bank passbook
  //      rather than a single opaque YTD lump sum.  Reconciliation row at
  //      the end absorbs any drift between the displayed monthly rate and
  //      the authoritative `accrued_days` reported by the backend.
  //    - Off in Lieu is claim-driven (section 3) — never a Jan-1 entitlement.
  if (!accrualBased && entitledDays > 0 && accountName !== 'Off in Lieu') {
    const eventDriven = isEventDrivenLeave(accountName);
    const grantDate = eventDriven
      ? resolveGrantDate(balance, year, periodStart)
      : periodStart;
    transactions.push({
      id: `${balance.id || accountName}-ent`,
      // Calendar-year grants anchor just after Jan 1 so they lead the year.
      // Event-driven grants sit at their real eligibility date so the ledger
      // shows exactly when the leave type became available.
      sortDate: eventDriven
        ? grantDate.getTime()
        : new Date(year, 0, 1).getTime() - 1,
      date: grantDate,
      kind: 'credit',
      title: eventDriven ? 'Entitlement granted' : `${year} entitlement`,
      description: eventDriven
        ? `${accountName} unlocked — granted when you became eligible`
        : `${accountName} entitlement granted`,
      reference: 'ENTITLEMENT',
      amount: entitledDays,
    });
  } else if (accrualBased && accruedDays > 0 && accrualDisplayEligible) {
    const monthsaryDates = computeMonthsaryDates({
      year,
      employmentStartDate: user?.employment_start_date,
      isPartTime,
    });

    if (monthsaryDates.length === 0) {
      // Fallback: no monthsaries derivable (e.g. employment_start_date
      // missing) — preserve the legacy single YTD line so users still see
      // their accrued balance.
      transactions.push({
        id: `${balance.id || accountName}-accr`,
        sortDate: new Date(year, 0, 1).getTime() - 1,
        date: periodStart,
        kind: 'credit',
        title: 'Accrued year-to-date',
        description: isPartTime
          ? 'Earned through monthly part-time accrual'
          : 'Earned through monthly anniversary accrual',
        reference: 'ACCRUAL',
        amount: accruedDays,
      });
    } else {
      // Use entitled / 12 as the displayed monthly rate when available
      // (this is the natural number employees expect to see — e.g. 14
      // entitled → 1.17 days/month).  Falls back to evenly distributing
      // accrued_days across the elapsed monthsaries when entitled is
      // unknown.
      const monthlyRate = entitledDays > 0
        ? entitledDays / 12
        : accruedDays / monthsaryDates.length;

      monthsaryDates.forEach((dt, idx) => {
        transactions.push({
          id: `${balance.id || accountName}-accr-${idx}`,
          sortDate: dt.getTime(),
          date: dt,
          kind: 'credit',
          title: 'Monthly accrual',
          description: isPartTime
            ? `Earned for ${dt.toLocaleDateString('en-GB', { month: 'long' })} (part-time)`
            : `Earned on your ${dt.toLocaleDateString('en-GB', { month: 'long' })} monthsary`,
          reference: 'ACCRUAL',
          amount: round1(monthlyRate),
        });
      });
    }
  }

  // 3. OIL credits (only relevant when the account is "Off in Lieu")
  //    Prefer per-claim / per-work-date rows so the ledger matches the
  //    employee's mental model ("I claimed OIL for that Saturday").  When
  //    claim payloads are missing from the year window but the balance still
  //    shows entitled days, surface a residual credit so Total earned is
  //    never silently zero against a real credit history.
  if (accountName === 'Off in Lieu') {
    let oilClaimCredits = 0;
    applications.forEach((app) => {
      if (getAppliedLeaveTypeName(app) !== 'Claim: Off in Lieu') return;
      const status = (app.leave_status || app.status || '').toLowerCase();
      if (status !== 'approved' && status !== 'auto_approved') return;
      const oilDates = Array.isArray(app.oil_dates) ? app.oil_dates : [];
      if (oilDates.length === 0) {
        // Fallback: single credit row using the claim's credited days
        const credited = toNumber(app.oil_credited_days ?? app.leave_total_days);
        if (credited > 0) {
          oilClaimCredits = round1(oilClaimCredits + credited);
          const isAuto = !!app.oil_auto_credited;
          transactions.push({
            id: `app-${app.id}-oilclaim`,
            sortDate: toSortKey(app.leave_applied_at || app.oil_claim_date || app.leave_start_date),
            date: app.leave_applied_at || app.oil_claim_date || app.leave_start_date,
            kind: 'credit',
            title: 'Off in Lieu earned',
            description: isAuto
              ? 'Auto-credited (public holiday / Saturday work)'
              : 'Approved overtime / public-holiday work',
            reference: isAuto ? 'OIL AUTO' : 'OIL CLAIM',
            amount: round1(credited),
            leaveApplication: app,
            status,
          });
        }
        return;
      }
      // Per-day breakdown -- one credit row per worked date in this statement year.
      // Out-of-year work dates are skipped; any gap vs balance.entitled_days is
      // closed by the residual credit below (not by dumping the full claim total
      // into this year).
      oilDates.forEach((entry, idx) => {
        const days = toNumber(entry?.days);
        if (days <= 0) return;
        const workDate = entry?.work_date || entry?.date || app.leave_start_date;
        // Skip work dates outside the statement year (claims can span years)
        if (workDate) {
          const ymd = String(workDate).match(/^(\d{4})/);
          if (ymd && Number(ymd[1]) !== year) return;
        }
        const expiry = entry?.expiry_date;
        oilClaimCredits = round1(oilClaimCredits + days);
        const isAuto = !!app.oil_auto_credited;
        transactions.push({
          id: `app-${app.id}-oil-${idx}`,
          sortDate: toSortKey(workDate),
          date: workDate,
          kind: 'credit',
          title: 'Off in Lieu earned',
          description: expiry
            ? `Worked overtime / public holiday · expires ${formatExpiryShort(expiry)}`
            : (isAuto
              ? 'Auto-credited (public holiday / Saturday work)'
              : 'Worked overtime / public holiday'),
          reference: isAuto ? 'OIL AUTO' : 'OIL CLAIM',
          amount: round1(days),
          leaveApplication: app,
          status,
        });
      });
    });

    const residual = round1(entitledDays - oilClaimCredits);
    if (residual >= 0.1) {
      transactions.push({
        id: `${balance.id || accountName}-oil-residual`,
        sortDate: new Date(year, 0, 1).getTime() - 1,
        date: periodStart,
        kind: 'credit',
        title: 'Off in Lieu earned',
        description: oilClaimCredits > 0
          ? 'Additional credited days not itemised in this statement'
          : 'Credited from approved Off in Lieu claim(s)',
        reference: 'OIL CREDIT',
        amount: residual,
      });
    }
  }

  // 4. Debits: consumption from this account by leave applications
  applications.forEach((app) => {
    const status = (app.leave_status || app.status || '').toLowerCase();
    const isApproved = APP_STATUSES_THAT_DEDUCT.has(status);
    const isPending = APP_STATUSES_PENDING.has(status);
    if (!isApproved && !isPending) return;

    const days = consumedDaysForAccount(app, accountName, year);
    if (days <= 0) return;

    const parsedStartDate = parseDateOnly(app.leave_start_date);
    const startsBeforeStatement = (
      parsedStartDate && parsedStartDate.getFullYear() < year
    );
    const startDate = startsBeforeStatement
      ? `${year}-01-01`
      : app.leave_start_date;
    const endDate = app.leave_end_date;
    const applied = getAppliedLeaveTypeName(app);
    const appliedDifferent = applied && applied !== accountName;
    // On the OIL account, "Annual Leave" as the title reads as a wrong-type
    // bug. Prefer "Used for Annual Leave" so the debit is clearly a draw
    // against this balance (OIL auto-consumed when AL was applied).
    const title = appliedDifferent
      ? `Used for ${applied}`
      : (applied || accountName);
    const description = appliedDifferent
      ? `Applied as ${applied} · drew from this balance`
      : buildLeaveDescription(app, accountName);

    transactions.push({
      id: `app-${app.id}-${accountName}`,
      sortDate: toSortKey(startDate),
      date: startDate,
      endDate,
      kind: isApproved ? 'debit' : 'pending',
      title,
      description,
      reference: `LV-${String(app.id).padStart(4, '0')}`,
      amount: days,
      leaveApplication: app,
      status,
    });
  });

  // Sort chronologically.  Stable: opening rows first, then by sortDate.
  transactions.sort((a, b) => {
    if (a.sortDate !== b.sortDate) return a.sortDate - b.sortDate;
    // Within same date: credits before debits
    const order = { opening: 0, credit: 1, debit: 2, pending: 3 };
    return (order[a.kind] ?? 9) - (order[b.kind] ?? 9);
  });

  // Does this account have a genuine "savings balance" basis -- i.e. an
  // opening carry-forward and/or earned credits to draw down?  Event-based
  // leaves that an employee simply *used* this year (e.g. Compassionate,
  // Marriage) often have no client-side entitlement ledger.  For those we
  // render a clean usage log instead of forcing a misleading running
  // balance (which would go negative) or inventing a reconciliation credit.
  const hasCreditTx = transactions.some((tx) => tx.kind === 'credit');
  const hasLedgerBasis = carriedForward > 0 || hasCreditTx;

  // Walk forward to compute the running approved balance.
  let running = 0;
  let totalCredits = 0;
  let totalDebits = 0;
  let totalPending = 0;
  let opening = 0;

  transactions.forEach((tx) => {
    if (tx.kind === 'opening') {
      running = round1(running + tx.amount);
      opening = round1(opening + tx.amount);
      tx.balance = running;
    } else if (tx.kind === 'credit') {
      running = round1(running + tx.amount);
      totalCredits = round1(totalCredits + tx.amount);
      tx.balance = running;
    } else if (tx.kind === 'debit') {
      running = round1(running - tx.amount);
      totalDebits = round1(totalDebits + tx.amount);
      // Usage-only accounts have no balance to draw down -- showing a
      // negative running total reads as an error.  Leave the balance blank
      // (rendered as "—") so the row is honestly a usage entry.
      tx.balance = hasLedgerBasis ? running : null;
    } else if (tx.kind === 'pending') {
      // Pending lines do not affect the approved running balance.
      totalPending = round1(totalPending + tx.amount);
      tx.balance = null;
    }
  });

  // Reconcile against the authoritative remaining balance from the API.
  // If they disagree (rare, e.g. backend granted manual adjustments), surface
  // an "Adjustment" line so the closing equals the real balance.
  const apiRemaining = round1(
    toNumber(balance.remaining_days ?? balance.remaining_balance)
  );

  if (hasLedgerBasis) {
    // Some accrual-based balances report total_available_days = carried + accrued,
    // so we can derive the "true" expected closing as remaining (api).  Inject a
    // reconciliation row when needed.
    const computedClosing = running;
    const drift = round1(apiRemaining - computedClosing);
    if (Math.abs(drift) >= 0.1) {
      const isCredit = drift > 0;
      transactions.push({
        id: `${balance.id || accountName}-adj`,
        sortDate: new Date(year, 11, 31).getTime() + 1,
        date: new Date(),
        kind: isCredit ? 'credit' : 'debit',
        title: 'Balance adjustment',
        description: 'Reconciles against your current balance',
        reference: 'ADJUSTMENT',
        amount: Math.abs(drift),
        balance: apiRemaining,
      });
      if (isCredit) totalCredits = round1(totalCredits + Math.abs(drift));
      else totalDebits = round1(totalDebits + Math.abs(drift));
      running = apiRemaining;
    }
  } else {
    // Usage-only account: the authoritative remaining is the closing figure.
    running = apiRemaining;
  }

  return {
    accountName,
    periodStart,
    periodEnd,
    opening,
    entitled: entitledDays,
    accrued: accruedDays,
    totalAvailable,
    totalCredits,
    totalDebits,
    totalPending,
    closing: running,
    apiRemaining,
    transactions,
    accrualBased,
    hasLedgerBasis,
    accrualDeferred,
    accrualEligibilityDate,
  };
};

/**
 * Format a YYYY-MM-DD or Date into "21 Mar 2026" without locale chatter.
 */
function formatExpiryShort(value) {
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch (_e) {
    return String(value);
  }
}

function buildLeaveDescription(app, accountName) {
  const start = app.leave_start_date;
  const end = app.leave_end_date;
  if (!start) return accountName;
  if (!end || start === end) {
    return `Leave on ${formatExpiryShort(start)}`;
  }
  return `Leave from ${formatExpiryShort(start)} → ${formatExpiryShort(end)}`;
}
